import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { USER_QUERY_KEYS } from '../api/endpoints'
import type { UserApi } from '../api/userApi'
import type { PaginatedApiResult, UserListParams } from '../types/api.types'
import type {
  PbxUser,
  UserAssignedRole,
  UserAssignedRolesResponse,
  UserAssociatedPbx,
  UserDetail,
  UserListItem,
  UserRole,
} from '../types/user.types'

export interface UseListUsersOptions {
  api: UserApi
  pbxId: number
  params?: UserListParams
  queryOptions?: Omit<
    UseQueryOptions<PaginatedApiResult<PbxUser> | null, Error>,
    'queryKey' | 'queryFn'
  >
}

export const useListUsers = ({
  api,
  pbxId,
  params = {},
  queryOptions,
}: UseListUsersOptions) => {
  const query = useQuery({
    queryKey: USER_QUERY_KEYS.list({
      pbxId,
      ...(params as Record<string, unknown>),
    }),
    queryFn: async () => {
      const response = await api.getPbxUsers(pbxId, params)
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      return response.data ?? null
    },
    placeholderData: (previousData) => previousData,
    ...queryOptions,
  })

  return {
    ...query,
    pbxUsers: query.data?.results ?? [],
    pagination: {
      count: query.data?.count ?? 0,
      next: query.data?.next,
      previous: query.data?.previous,
    },
  }
}

export interface UseListGlobalUsersOptions {
  api: UserApi
  params?: UserListParams & { pbx_id?: number }
  queryOptions?: Omit<
    UseQueryOptions<PaginatedApiResult<UserListItem> | null, Error>,
    'queryKey' | 'queryFn'
  >
}

export const useListGlobalUsers = ({
  api,
  params = {},
  queryOptions,
}: UseListGlobalUsersOptions) => {
  const query = useQuery({
    queryKey: USER_QUERY_KEYS.globalList(params as Record<string, unknown>),
    queryFn: async (): Promise<PaginatedApiResult<UserListItem> | null> => {
      const response = await api.getUsers(params)
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      return response.data ?? null
    },
    ...queryOptions,
  })

  return {
    ...query,
    users: query.data?.results ?? [],
    pagination: {
      count: query.data?.count ?? 0,
      next: query.data?.next,
      previous: query.data?.previous,
    },
  }
}

export interface UseGetUserOptions {
  api: UserApi
  userId: number
  queryOptions?: Omit<
    UseQueryOptions<UserDetail | null, Error>,
    'queryKey' | 'queryFn'
  >
}

export const useGetUser = ({
  api,
  userId,
  queryOptions,
}: UseGetUserOptions) => {
  const query = useQuery({
    queryKey: USER_QUERY_KEYS.detail(userId),
    queryFn: async () => {
      const response = await api.getUser(userId)
      if (!response.ok) {
        throw new Error('Failed to fetch user')
      }
      return response.data ?? null
    },
    enabled: userId > 0,
    ...queryOptions,
  })

  return {
    ...query,
    user: query.data ?? null,
  }
}

type AssignedRolesEnvelope = {
  roles?: unknown
}

function normalizeAssignedRolesResponse(
  data:
    | UserAssignedRolesResponse
    | UserAssignedRole[]
    | AssignedRolesEnvelope
    | null,
): UserAssignedRole[] {
  if (!data) return []

  const rawRoles = Array.isArray(data)
    ? data
    : Array.isArray((data as AssignedRolesEnvelope).roles)
      ? ((data as AssignedRolesEnvelope).roles as unknown[])
      : []

  return rawRoles
    .map((role): UserAssignedRole | null => {
      if (!role || typeof role !== 'object') return null
      const value = role as Record<string, unknown>
      const name = typeof value.name === 'string' ? value.name : ''
      const type = typeof value.type === 'string' ? value.type : ''
      if (!name && !type) return null
      return { name, type }
    })
    .filter((role): role is UserAssignedRole => role !== null)
}

export interface UseGetUserAssignedRolesOptions {
  api: UserApi
  userId: number
  queryOptions?: Omit<
    UseQueryOptions<UserAssignedRole[], Error>,
    'queryKey' | 'queryFn'
  >
}

export const useGetUserAssignedRoles = ({
  api,
  userId,
  queryOptions,
}: UseGetUserAssignedRolesOptions) => {
  const query = useQuery({
    queryKey: USER_QUERY_KEYS.assignedRoles(userId),
    queryFn: async () => {
      const response = await api.getUserAssignedRoles(userId)
      if (!response.ok) {
        throw new Error('Failed to fetch assigned user roles')
      }

      return normalizeAssignedRolesResponse(
        response.data as
          | UserAssignedRolesResponse
          | UserAssignedRole[]
          | AssignedRolesEnvelope
          | null,
      )
    },
    enabled: userId > 0,
    ...queryOptions,
  })

  return {
    ...query,
    assignedRoles: query.data ?? [],
  }
}

type UserPbxesEnvelope = {
  pbxes?: unknown
  results?: unknown
}

function hasArrayPbxes(
  value: PaginatedApiResult<UserAssociatedPbx> | UserPbxesEnvelope,
): value is { pbxes: unknown[] } {
  return Array.isArray((value as { pbxes?: unknown }).pbxes)
}

function hasArrayUserPbxResults(
  value: PaginatedApiResult<UserAssociatedPbx> | UserPbxesEnvelope,
): value is { results: unknown[] } {
  return Array.isArray((value as { results?: unknown }).results)
}

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function toStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function toNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function toNumberArray(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined
  const numericValues = value
    .map((item) => toNumberOrNull(item))
    .filter((item): item is number => item != null)
  return numericValues.length > 0 ? numericValues : []
}

function toUserAssociatedPbx(value: unknown): UserAssociatedPbx | null {
  if (!value || typeof value !== 'object') return null

  const data = value as Record<string, unknown>
  const pbxId = toNumberOrNull(data.id ?? data.pbx_id)
  if (pbxId == null) return null

  const pbxName = toStringOrEmpty(data.name ?? data.pbx_name).trim()

  return {
    id: pbxId,
    name: pbxName.length > 0 ? pbxName : `PBX ${pbxId}`,
    sending_fax_email: toNullableString(data.sending_fax_email),
    sending_fax_number: toNullableString(data.sending_fax_number),
    pin: toNumberOrNull(data.pin),
    fax_title: toNullableString(data.fax_title),
    user_groups: toNumberArray(data.user_groups),
  }
}

function normalizeUserPbxListResponse(
  data:
    | UserAssociatedPbx[]
    | PaginatedApiResult<UserAssociatedPbx>
    | UserPbxesEnvelope
    | null,
): UserAssociatedPbx[] {
  if (!data) return []

  const rawItems = Array.isArray(data)
    ? data
    : hasArrayPbxes(data)
      ? data.pbxes
      : hasArrayUserPbxResults(data)
        ? data.results
        : []

  return rawItems
    .map((item) => toUserAssociatedPbx(item))
    .filter((item): item is UserAssociatedPbx => item != null)
}

export interface UseListUserPbxesOptions {
  api: UserApi
  userId: number
  queryOptions?: Omit<
    UseQueryOptions<UserAssociatedPbx[], Error>,
    'queryKey' | 'queryFn'
  >
}

export const useListUserPbxes = ({
  api,
  userId,
  queryOptions,
}: UseListUserPbxesOptions) => {
  const query = useQuery({
    queryKey: USER_QUERY_KEYS.userPbxList(userId),
    queryFn: async () => {
      const response = await api.getUserPbxes(userId)
      if (!response.ok) {
        throw new Error('Failed to fetch user PBX associations')
      }

      return normalizeUserPbxListResponse(
        response.data as
          | UserAssociatedPbx[]
          | PaginatedApiResult<UserAssociatedPbx>
          | UserPbxesEnvelope
          | null,
      )
    },
    enabled: userId > 0,
    ...queryOptions,
  })

  return {
    ...query,
    userPbxes: query.data ?? [],
  }
}

type RolesEnvelope = {
  roles?: unknown
  results?: unknown
}

function hasArrayResults(
  value: PaginatedApiResult<UserRole> | RolesEnvelope,
): value is { results: UserRole[] } {
  return Array.isArray((value as { results?: unknown }).results)
}

function hasArrayRoles(
  value: PaginatedApiResult<UserRole> | RolesEnvelope,
): value is { roles: UserRole[] } {
  return Array.isArray((value as { roles?: unknown }).roles)
}

function normalizeRoleListResponse(
  data: UserRole[] | PaginatedApiResult<UserRole> | RolesEnvelope | null,
): UserRole[] {
  if (!data) return []
  if (Array.isArray(data)) return data

  if (hasArrayResults(data)) {
    return data.results as UserRole[]
  }

  if (hasArrayRoles(data)) {
    return data.roles as UserRole[]
  }

  return []
}

export interface UseListUserRolesOptions {
  api: UserApi
  queryOptions?: Omit<
    UseQueryOptions<UserRole[], Error>,
    'queryKey' | 'queryFn'
  >
}

export const useListUserRoles = ({
  api,
  queryOptions,
}: UseListUserRolesOptions) => {
  const query = useQuery({
    queryKey: USER_QUERY_KEYS.roles(),
    queryFn: async () => {
      const response = await api.getUserRoles()
      if (!response.ok) {
        throw new Error('Failed to fetch user roles')
      }

      return normalizeRoleListResponse(
        response.data as
          UserRole[] | PaginatedApiResult<UserRole> | RolesEnvelope | null,
      )
    },
    ...queryOptions,
  })

  return {
    ...query,
    roles: query.data ?? [],
  }
}
